import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { 
  ArrowLeft, Radio, Settings, BarChart3, RefreshCw,
  Tv, Calendar, Menu, Sliders, Layout, Film, Megaphone, Briefcase, Users, Trophy, Search,
  Newspaper, PenTool, Rss, FileText, User, Image, Share2, Camera, Type, Shield, Star, Vote,
  ExternalLink, Bell, Headphones, BookOpen, Clock, Monitor, PlayCircle, Globe, Timer, Activity,
  Download, Award, Video
} from "lucide-react";
import axios from "axios";

// Import tab components from admin folder
import {
  OverviewTab,
  ChannelHubTab,
  BroadcastsTab,
  RecordingsTab,
  AdsTab,
  ScheduleTab,
  HomepageTab,
  MenuTab,
  TickerTab,
  SettingsTab,
  CareersTab,
  SponsorsTab,
  EventsTab,
  PageSEOTab,
  NewsTab,
  WritersTab,
  RSSTab,
  CustomPagesTab,
  FeaturedAthleteTab,
  GalleriesTab,
  SocialTab,
  PhotographersTab,
  FontsTab,
  TeamsTab,
  TopStoriesTab,
  HomepagePollTab,
  WatchTab,
  FanPollsTab,
  AdBreaksTab
} from "./admin";
import { NotificationsTab } from "./admin/NotificationsTab";
import { PodcastsTab } from "./admin/PodcastsTab";
import { RecordBookTab } from "./admin/RecordBookTab";
import { ComingSoonTab } from "./admin/ComingSoonTab";
import { DemosTab } from "./admin/DemosTab";
import { SportsTab } from "./admin/SportsTab";
import { LeagueManagerTab } from "./admin/LeagueManagerTab";
import { SyndicationTab } from "./admin/SyndicationTab";
import { ConferenceNetworkTab } from "./admin/ConferenceNetworkTab";
import { VideoAdsTab } from "./admin/VideoAdsTab";
import { AnalyticsTab } from "./admin/AnalyticsTab";
import { ASNPlusAdminTab } from "./admin/ASNPlusAdminTab";
import YouTubeDownloaderTab from "./admin/YouTubeDownloaderTab";
import { TriviaTab } from "./admin/TriviaTab";
import { MeetingTab } from "./admin/MeetingTab";
import { ScoreboardTab } from "./admin/ScoreboardTab";
import { StaffTab } from "./admin/StaffTab";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => {
  const token = localStorage.getItem("auth_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [channels, setChannels] = useState([]);
  const [stats, setStats] = useState(null);
  const [liveStreams, setLiveStreams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authStatus, setAuthStatus] = useState({ auth_required: true }); // Default to secure

  useEffect(() => {
    const verifyAuth = async () => {
      const token = localStorage.getItem("auth_token");
      if (!token) { navigate("/login"); return; }
      try {
        await axios.get(`${API}/auth/verify`, { headers: getAuthHeader() });
        setIsAuthenticated(true);
        // Check if auth is actually required
        const statusRes = await axios.get(`${API}/auth/status`);
        setAuthStatus(statusRes.data);
      } catch (error) {
        localStorage.removeItem("auth_token");
        navigate("/login");
      }
    };
    verifyAuth();
  }, [navigate]);

  const fetchData = async () => {
    try {
      const [channelsRes, statsRes, liveRes] = await Promise.all([
        axios.get(`${API}/channels/admin/all`, { headers: getAuthHeader() }),
        axios.get(`${API}/stats`),
        axios.get(`${API}/broadcasts/live`)
      ]);
      setChannels(channelsRes.data);
      setStats(statsRes.data);
      setLiveStreams(liveRes.data);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
      const interval = setInterval(fetchData, 10000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  const handleRefresh = () => { setRefreshing(true); fetchData(); };
  const handleLogout = () => { localStorage.removeItem("auth_token"); navigate("/login"); };

  const tabGroups = [
    {
      name: "Dashboard",
      tabs: [
        { id: "overview", name: "Overview", icon: BarChart3 },
        { id: "analytics", name: "Analytics", icon: Activity },
        { id: "channel-hub", name: "Channels", icon: Tv },
        { id: "staff", name: "Staff", icon: Users },
        { id: "notifications", name: "Notifications", icon: Bell },
        { id: "settings", name: "Settings", icon: Settings }
      ]
    },
    {
      name: "Broadcast Manager",
      tabs: [
        { id: "broadcasts", name: "Scheduler", icon: Calendar },
        { id: "recordings", name: "Recordings", icon: Film },
        { id: "scoreboard", name: "Ticker", icon: Activity },
        { id: "watch", name: "On-Demand", icon: PlayCircle },
        { id: "leagues", name: "League Manager", icon: Trophy },
        { id: "events", name: "Events", icon: Trophy },
        { id: "teams", name: "Team Manager", icon: Shield },
        { id: "sports", name: "Sports", icon: Trophy },
        { id: "record-book", name: "Record Book", icon: BookOpen },
        { id: "fan-polls", name: "Fan Polls", icon: Vote },
        { id: "trivia", name: "Trivia", icon: Award },
        { id: "ad-breaks", name: "Ad Breaks", icon: Timer }
      ]
    },
    {
      name: "Homepage Manager",
      tabs: [
        { id: "homepage", name: "Homepage", icon: Layout },
        { id: "menu", name: "Menu", icon: Menu },
        { id: "homepage-poll", name: "Homepage Poll", icon: Vote },
        { id: "featured-athlete", name: "Featured Athlete", icon: User },
        { id: "ticker", name: "Ticker", icon: Sliders }
      ]
    },
    {
      name: "Sponsor Manager",
      tabs: [
        { id: "sponsors", name: "Sponsors", icon: Users },
        { id: "ads", name: "Ads", icon: Megaphone },
        { id: "video-ads", name: "Pre/Mid Roll Ads", icon: Film }
      ]
    },
    {
      name: "News Manager",
      tabs: [
        { id: "news", name: "News", icon: Newspaper },
        { id: "top-stories", name: "Top Stories", icon: Star },
        { id: "writers", name: "Writers", icon: PenTool },
        { id: "rss", name: "News Feeds", icon: Rss },
        { id: "podcasts", name: "Podcasts", icon: Headphones }
      ]
    },
    {
      name: "Photos",
      tabs: [
        { id: "galleries", name: "Galleries", icon: Image },
        { id: "photographers", name: "Photographers", icon: Camera }
      ]
    },
    {
      name: "Other",
      tabs: [
        { id: "social", name: "Social Media", icon: Share2 },
        { id: "custom-pages", name: "Custom Pages", icon: FileText },
        { id: "fonts", name: "Custom Fonts", icon: Type },
        { id: "page-seo", name: "Page SEO", icon: Search },
        { id: "careers", name: "Careers", icon: Briefcase },
        { id: "coming-soon", name: "Coming Soon", icon: Clock },
        { id: "demos", name: "Feature Demos", icon: Monitor }
      ]
    },
    {
      name: "ASN Plus",
      tabs: [
        { id: "asn-plus", name: "ASN Plus", icon: Star }
      ]
    },
    {
      name: "Tools",
      tabs: [
        { id: "yt-downloader", name: "YT Downloader", icon: Download },
        { id: "meeting-space", name: "Meeting Space", icon: Video }
      ]
    }
  ];

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Security Warning Banner - shown when auth is not enabled */}
      {!authStatus.auth_required && (
        <div className="bg-amber-500/20 border-b border-amber-500/50 px-4 py-3">
          <div className="max-w-[1600px] mx-auto flex items-center gap-3">
            <Shield className="w-5 h-5 text-amber-400 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-amber-200 text-sm font-medium">
                Admin panel is currently <span className="font-bold">UNSECURED</span>. Anyone can access it.
              </p>
              <p className="text-amber-200/70 text-xs mt-0.5">
                Go to <button onClick={() => setActiveTab('settings')} className="underline hover:text-white">Settings</button> to set up admin credentials and secure your site.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-black border-b border-white/10 sticky top-0 z-40">
        <div className="max-w-[1600px] mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors">
                <ArrowLeft className="w-5 h-5" /><span className="text-sm">Back</span>
              </Link>
              <div className="h-6 w-px bg-white/10"></div>
              <h1 className="font-secondary text-xl font-bold uppercase tracking-tight text-white">Admin</h1>
            </div>
            <div className="flex items-center gap-4">
              <button 
                onClick={() => window.open('/', '_blank')} 
                className="flex items-center gap-2 px-3 py-1.5 bg-orange-500 hover:bg-orange-600 text-black text-xs font-bold uppercase transition-colors"
                data-testid="view-site-btn"
              >
                <ExternalLink className="w-3 h-3" />
                View Site
              </button>
              <button onClick={handleRefresh} disabled={refreshing} className="flex items-center gap-2 text-zinc-400 hover:text-white disabled:opacity-50">
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
              <span className="text-xs text-zinc-500 hidden sm:inline">{localStorage.getItem("auth_email")}</span>
              <button onClick={handleLogout} className="text-xs text-zinc-500 hover:text-white">Logout</button>
              <img src="/asn-logo-main.png" alt="ASN" className="h-8 w-auto" style={{ background: 'transparent' }} />
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Navigation */}
          <nav className="lg:w-56 flex-shrink-0">
            <div className="bg-zinc-900/50 border border-white/5 p-2 sticky top-24 max-h-[calc(100vh-120px)] overflow-y-auto">
              {tabGroups.map((group, groupIdx) => (
                <div key={group.name} className={groupIdx > 0 ? 'mt-4 pt-4 border-t border-white/5' : ''}>
                  <div className="px-4 py-2 text-[10px] font-bold uppercase tracking-widest text-zinc-600">
                    {group.name}
                  </div>
                  {group.tabs.map((tab) => {
                    const Icon = tab.icon;
                    return (
                      <button 
                        key={tab.id} 
                        onClick={() => setActiveTab(tab.id)} 
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors ${
                          activeTab === tab.id 
                            ? 'bg-white text-black' 
                            : 'text-zinc-400 hover:text-white hover:bg-white/5'
                        }`}
                      >
                        <Icon className="w-4 h-4" />{tab.name}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </nav>

          {/* Main Content Area */}
          <main className="flex-1 min-w-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
              </div>
            ) : (
              <>
                {activeTab === "overview" && <OverviewTab stats={stats} channels={channels} liveStreams={liveStreams} />}
                {activeTab === "analytics" && <AnalyticsTab />}
                {activeTab === "channel-hub" && <ChannelHubTab channels={channels} onRefresh={fetchData} />}
                {activeTab === "broadcasts" && <BroadcastsTab channels={channels} onRefresh={fetchData} />}
                {activeTab === "syndication" && <SyndicationTab />}
                {activeTab === "conference-networks" && <ConferenceNetworkTab />}
                {activeTab === "watch" && <WatchTab />}
                {activeTab === "events" && <EventsTab />}
                {activeTab === "recordings" && <RecordingsTab />}
                {activeTab === "news" && <NewsTab />}
                {activeTab === "top-stories" && <TopStoriesTab />}
                {activeTab === "writers" && <WritersTab />}
                {activeTab === "rss" && <RSSTab />}
                {activeTab === "ads" && <AdsTab />}
                {activeTab === "video-ads" && <VideoAdsTab />}
                {activeTab === "sponsors" && <SponsorsTab />}
                {activeTab === "schedule" && <ScheduleTab />}
                {activeTab === "homepage" && <HomepageTab />}
                {activeTab === "homepage-poll" && <HomepagePollTab />}
                {activeTab === "featured-athlete" && <FeaturedAthleteTab />}
                {activeTab === "galleries" && <GalleriesTab />}
                {activeTab === "photographers" && <PhotographersTab />}
                {activeTab === "social" && <SocialTab />}
                {activeTab === "custom-pages" && <CustomPagesTab />}
                {activeTab === "fonts" && <FontsTab />}
                {activeTab === "teams" && <TeamsTab />}
                {activeTab === "sports" && <SportsTab />}
                {activeTab === "leagues" && <LeagueManagerTab />}
                {activeTab === "menu" && <MenuTab />}
                {activeTab === "ticker" && <TickerTab />}
                {activeTab === "page-seo" && <PageSEOTab />}
                {activeTab === "careers" && <CareersTab />}
                {activeTab === "settings" && <SettingsTab />}
                {activeTab === "notifications" && <NotificationsTab />}
                {activeTab === "podcasts" && <PodcastsTab />}
                {activeTab === "record-book" && <RecordBookTab />}
                {activeTab === "coming-soon" && <ComingSoonTab />}
                {activeTab === "demos" && <DemosTab />}
                {activeTab === "fan-polls" && <FanPollsTab />}
                {activeTab === "ad-breaks" && <AdBreaksTab />}
                {activeTab === "asn-plus" && <ASNPlusAdminTab />}
                {activeTab === "yt-downloader" && <YouTubeDownloaderTab />}
                {activeTab === "trivia" && <TriviaTab />}
                {activeTab === "meeting-space" && <MeetingTab />}
                {activeTab === "scoreboard" && <ScoreboardTab />}
                {activeTab === "staff" && <StaffTab />}
              </>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
